import {Platform, StyleSheet} from 'react-native';
import React, {useMemo} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {H6Text} from '@buncha/components/core/text';
import {HBox} from '@buncha/components/core/view';
import If from '@buncha/components/conditional/If';
import {ItemType} from '@buncha/itemDetails/types/itemReplacement';
import LeafBorderIcon from '@buncha/components/core/icon/LeafBorderIcon';
import PersonRoundIcon from '@buncha/components/core/icon/PersonRoundIcon';

interface Props {
  size?: number;
  itemType?: ItemType;
  icon?: React.ReactNode;
  backgroudColor?: string;
  typeName?: string;
  textColor?: string;
  isOrganic?: boolean;
}

const ItemTypePill = (props: Props) => {
  const theme = useColorThemeType();
  const muliFont = useFontTheme();

  const {itemType, size, isOrganic, typeName, backgroudColor, textColor} =
    props;

  const getItemTypeIcon = useMemo(() => {
    if (itemType === ItemType.Alcoholic)
      return (
        <PersonRoundIcon
          fill={theme?.text.red[100]}
          height={size ?? 14}
          width={size ?? 14}
          viewBox="-3 -4 35 35"
        />
      );
    else if (isOrganic)
      return (
        <LeafBorderIcon
          fill={theme?.text.yellow[100]}
          height={size ?? 14}
          width={size ?? 14}
          viewBox="4 0 15 25"
        />
      );
  }, [isOrganic, itemType, size, theme?.text.red, theme?.text.yellow]);

  const getText = useMemo(() => {
    if (typeName) return typeName;
    if (itemType === ItemType.Alcoholic) return 'Age Sensitive';
    else if (isOrganic) return 'Organic';
    return undefined;
  }, [isOrganic, itemType, typeName]);

  const getItemTypeColor = useMemo(() => {
    if (itemType === ItemType.Alcoholic)
      return {
        bgColor: backgroudColor ?? theme?.background.red[200],
        textColor: textColor ?? theme?.text.red[100],
      };
    return {
      bgColor: backgroudColor ?? theme?.background.blue[300],
      textColor: textColor ?? theme?.text.yellow[100],
    };
  }, [
    backgroudColor,
    itemType,
    textColor,
    theme?.background.blue,
    theme?.background.red,
    theme?.text.red,
    theme?.text.yellow,
  ]);

  return (
    <If condition={getText?.length}>
      <HBox
        style={[
          styles.highlightText,
          {
            backgroundColor: getItemTypeColor.bgColor,
          },
        ]}>
        <If condition={props?.icon}>{props?.icon}</If>
        <If condition={!props?.icon}>{getItemTypeIcon}</If>

        <H6Text
          style={[
            {
              fontFamily: muliFont[700].normal,
              color: getItemTypeColor.textColor,
              fontSize: props?.size,
            },
          ]}>
          {getText}
        </H6Text>
      </HBox>
    </If>
  );
};

export default React.memo(ItemTypePill);

const styles = StyleSheet.create({
  highlightText: {
    borderRadius: 20,
    paddingHorizontal: 10,
    gap: 5,
    marginBottom: Platform.OS === 'android' ? -5 : 0,
    alignSelf: 'flex-start',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: '1%',
  },
});
